import { useState, useRef, useCallback, useEffect } from 'react';

const MAX_PHOTO_RETRIES = 5;

interface PhotoImgProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src: string;
  /**
   * Lower-quality fallback URL tried once before the retry cycle begins
   * (e.g. photoUrl when optimizedUrl or thumbnailUrl fails).
   * Skipped silently when equal to `src`.
   */
  fallbackSrc?: string;
}

/**
 * Drop-in <img> replacement for S3-hosted apartment photos.
 *
 * On load failure:
 * 1. Switches to `fallbackSrc` (once) if it differs from the active URL.
 * 2. Retries the active URL up to MAX_PHOTO_RETRIES times with exponential
 *    back-off: 1 s → 2 s → 4 s → 8 s → 16 s.
 * 3. Stops silently after retries are exhausted — the image stays broken
 *    rather than looping forever.
 *
 * Retry is implemented by changing `retryKey`, which causes React to unmount
 * and remount the <img>, forcing the browser to issue a fresh HTTP request.
 */
export function PhotoImg({ src: srcProp, fallbackSrc, ...imgProps }: PhotoImgProps) {
  const [activeSrc, setActiveSrc] = useState(srcProp);
  const [retryKey, setRetryKey] = useState(0);
  const attemptsRef = useRef(0);
  const fallbackUsedRef = useRef(false);

  // Reset when the parent changes the source (e.g. after a photo upload).
  useEffect(() => {
    setActiveSrc(srcProp);
    setRetryKey(0);
    attemptsRef.current = 0;
    fallbackUsedRef.current = false;
  }, [srcProp]);

  const handleError = useCallback<React.ReactEventHandler<HTMLImageElement>>(() => {
    // Step 1 — try the fallback URL once before beginning the retry cycle.
    if (!fallbackUsedRef.current && fallbackSrc && activeSrc !== fallbackSrc) {
      fallbackUsedRef.current = true;
      attemptsRef.current = 0;
      setActiveSrc(fallbackSrc);
      return;
    }

    // Step 2 — retry with exponential back-off.
    const attempt = attemptsRef.current;
    if (attempt >= MAX_PHOTO_RETRIES) return; // exhausted — stop retrying

    attemptsRef.current = attempt + 1;
    const delayMs = 1_000 * 2 ** attempt; // 1 s, 2 s, 4 s, 8 s, 16 s
    setTimeout(() => setRetryKey(k => k + 1), delayMs);
  }, [activeSrc, fallbackSrc]);

  return <img key={retryKey} src={activeSrc} onError={handleError} {...imgProps} />;
}
